/**
 * COMMAND DECK — the room's "command center" (owner 2026-10-03): ONE Slack-style bordered box with three decks.
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ topLeft (📎 attach)               topRight (modes · ✦)   │  upper deck — toolbar
 *   │ children (the prompt input)                              │  middle deck
 *   │ bottomLeft (dock toggle · chips)     bottomRight (Send)  │  lower deck — footer
 *   └──────────────────────────────────────────────────────────┘
 *
 * Presentational + reusable (the network UI mounts the same deck with its own controls). Slack's look: a single
 * rounded container, 1px border that darkens on focus, shaded upper + lower decks around a white input, compact icon buttons, no inner cards or pills. An optional
 * ``alertBand`` sits just above the box (approval / notices) — not part of the composer.
 */
import React from 'react';

export const COMMAND_DECK_BOX = [
  'rounded-lg border border-gray-300 bg-white',
  'transition-[border-color,box-shadow] duration-150 ease-out',
  'hover:border-gray-400',
  'focus-within:border-gray-500 focus-within:shadow-[0_0_0_1px_rgba(29,28,29,0.13),0_1px_3px_rgba(0,0,0,0.08)]',
].join(' ');

/** Slack's toolbar/footer shade — the upper + lower decks are tinted, the input deck stays white. */
export const DECK_SHADE = 'bg-[#f8f8f8]';

/** Slack-style compact icon button (28px, rounded, gray → hover tint). Use for deck actions. */
export const DECK_ICON_BUTTON = [
  'inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded px-1.5 text-[12px] text-gray-600',
  'transition-colors hover:bg-gray-200/70 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40',
].join(' ');

export default function CommandDeck({
  topLeft = null,
  topRight = null,
  children = null,
  bottomLeft = null,
  bottomRight = null,
  alertBand = null,
  className = '',
  testId = 'command-deck',
}) {
  return (
    <div className="w-full min-w-0" data-testid={`${testId}-wrap`}>
      {alertBand ? <div className="mb-1.5" data-testid={`${testId}-alert`}>{alertBand}</div> : null}
      <div className={`${COMMAND_DECK_BOX} ${className}`.trim()} role="group" aria-label="composer" data-testid={testId}>
        {(topLeft || topRight) ? (
          <div className={`flex min-h-0 items-center justify-between gap-2 rounded-t-lg ${DECK_SHADE} px-1.5 py-1`} data-testid={`${testId}-top`}>
            <div className="flex min-w-0 flex-1 items-center gap-1">{topLeft}</div>
            <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">{topRight}</div>
          </div>
        ) : null}
        <div className="min-w-0 px-3 py-1.5" data-testid={`${testId}-input`}>{children}</div>
        {(bottomLeft || bottomRight) ? (
          <div className={`flex min-h-0 items-center justify-between gap-2 rounded-b-lg ${DECK_SHADE} px-1.5 py-1`} data-testid={`${testId}-bottom`}>
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">{bottomLeft}</div>
            <div className="flex shrink-0 items-center gap-1">{bottomRight}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
